import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const STATUS_CFG = {
  present: { letter: "P", color: "#059669", bg: "#ECFDF5" },
  absent: { letter: "A", color: "#E60000", bg: "#FEF2F2" },
  retard: { letter: "R", color: "#D97706", bg: "#FFFBEB" },
  excuse: { letter: "E", color: "#0EA5E9", bg: "#EFF6FF" },
};

export default function EducationPresencesMonthly() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [classeId, setClasseId] = useState("all");
  const [classes, setClasses] = useState([]);
  const [data, setData] = useState({ days: [], eleves: [] });

  const loadClasses = async () => {
    try {
      const r = await api.get("/chef/education/classes");
      setClasses(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const r = await api.get(`/chef/education/presences/monthly?year=${year}&month=${month}&classe_id=${classeId}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadClasses(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, classeId]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const dayLabels = useMemo(() => {
    return data.days.map((d) => {
      const dt = new Date(d);
      return { iso: d, dom: dt.getDate(), dow: dt.toLocaleDateString("fr-FR", { weekday: "short" }) };
    });
  }, [data.days]);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Présences mensuelles"
        title={`${MONTHS[month - 1]} ${year}`}
        sub="Vue synthétique des présences sur le mois. Filtrez par classe pour affiner."
        right={
          <div className="flex gap-2 items-center">
            <Button variant="outline" onClick={prev} data-testid="prev-month-btn" className="h-11 px-3"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={next} data-testid="next-month-btn" className="h-11 px-3"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        <div className="bg-white border border-[#DEE2E6] p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="monthly-filters">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Mois</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="h-11 mt-1" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Année</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="h-11 mt-1" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Classe</Label>
            <Select value={classeId} onValueChange={setClasseId}>
              <SelectTrigger className="h-11 mt-1" data-testid="classe-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="none">Sans classe</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white border border-[#DEE2E6]" data-testid="monthly-table-wrap">
          <div className="p-5 border-b border-[#DEE2E6] flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold">
            <CalendarRange className="h-4 w-4 text-[#002FA7]" /> {data.eleves.length} élève(s) · {data.days.length} jour(s) pointé(s)
          </div>
          {(data.eleves.length === 0 || data.days.length === 0) ? (
            <div className="p-10 text-center text-sm text-[#868E96]" data-testid="monthly-empty">
              {data.eleves.length === 0 ? "Aucun élève." : "Aucune présence saisie ce mois-ci."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#F8F9FA]">
                    <th className="text-left p-3 sticky left-0 bg-[#F8F9FA] border-r border-[#DEE2E6]" style={{ minWidth: 200 }}>Élève</th>
                    {dayLabels.map((d) => (
                      <th key={d.iso} className="p-1 text-center" style={{ minWidth: 36 }}>
                        <div className="text-[9px] uppercase text-[#868E96]">{d.dow.slice(0, 3)}</div>
                        <div className="font-bold font-mono">{d.dom}</div>
                      </th>
                    ))}
                    {["present", "absent", "retard", "excuse"].map((s) => (
                      <th key={s} className="p-2 text-center border-l border-[#DEE2E6]" style={{ minWidth: 50, color: STATUS_CFG[s].color }}>
                        {STATUS_CFG[s].letter}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="monthly-rows">
                  {data.eleves.map((e) => (
                    <tr key={e.id} className="border-t border-[#F8F9FA] hover:bg-[#F8F9FA]" data-testid={`monthly-row-${e.id}`}>
                      <td className="p-3 sticky left-0 bg-white border-r border-[#DEE2E6]">
                        <div className="font-bold">{e.prenom} {e.nom}</div>
                        <div className="text-[10px] text-[#868E96]">{e.classe_nom || "—"}</div>
                      </td>
                      {dayLabels.map((d) => {
                        const st = e.by_date?.[d.iso];
                        const cfg = STATUS_CFG[st];
                        return (
                          <td key={d.iso} className="p-1 text-center">
                            {cfg ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 font-bold text-[10px]" style={{ background: cfg.bg, color: cfg.color }}>{cfg.letter}</span>
                            ) : <span className="text-[#DEE2E6]">·</span>}
                          </td>
                        );
                      })}
                      {["present", "absent", "retard", "excuse"].map((s) => (
                        <td key={s} className="p-2 text-center border-l border-[#DEE2E6] font-mono font-bold" style={{ color: STATUS_CFG[s].color }}>
                          {e.total?.[s] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-[10px] text-[#868E96] uppercase tracking-wider">
          Légende : <span className="font-bold" style={{ color: "#059669" }}>P</span> présent · <span className="font-bold" style={{ color: "#E60000" }}>A</span> absent · <span className="font-bold" style={{ color: "#D97706" }}>R</span> retard · <span className="font-bold" style={{ color: "#0EA5E9" }}>E</span> excusé
        </div>
      </div>
    </div>
  );
}
